import axios from 'axios';

import type {
  CreateDataQueryResponse,
  GetCountByQueryResponse,
  GetDataByQueryResponse,
  GetDataIdsByQuery,
  GetDataQueryRequest,
  GetDataQueryResponse,
  GetSummaryRowResponse,
  QueryIdRequestBody,
} from '../types/api/data';
import {isApiException} from '../utils/isApiException';
import {AXIOS_BASE_CONFIG} from './axiosConfig';
import type {ApiContext} from './types';

export function createDataApi(ctx: ApiContext) {
  const BASE = `/v6/data-grid/${ctx.gridCode}`;

  const buildDataUrl = (
    dataQueryId: string,
    suffix: string,
    params: {offset: number; limit: number; hasSmartSearch: boolean}
  ) => {
    const search = new URLSearchParams({
      offset: String(params.offset),
      limit: String(params.limit),
      ...(params.hasSmartSearch && {smartSearch: 'true'}),
    });
    return `${BASE}/data/${dataQueryId}${suffix}?${search}`;
  };

  return {
    createDataQuery: (params: QueryIdRequestBody, signal?: AbortSignal) =>
      axios
        .post<CreateDataQueryResponse>(`${BASE}/data-query`, params, {...AXIOS_BASE_CONFIG, signal})
        .then((response) => response.data)
        .catch(ctx.handleApiError),
    getDataByQuery: (
      dataQueryId: string,
      offset: number,
      limit: number,
      hasSmartSearch: boolean,
      columns: string[],
      signal?: AbortSignal
    ) =>
      axios
        .post<GetDataByQueryResponse>(
          buildDataUrl(dataQueryId, '', {offset, limit, hasSmartSearch}),
          {columns},
          {...AXIOS_BASE_CONFIG, signal}
        )
        .then((response) => response.data.rows)
        .catch(ctx.handleApiError),
    getSummaryRow: (dataQueryId: string) =>
      axios
        .get<GetSummaryRowResponse>(`${BASE}/data/${dataQueryId}/summary`, AXIOS_BASE_CONFIG)
        .then((response) => response.data.summaryRow.cells)
        .catch(ctx.handleApiError),
    getCountByQuery: (dataQueryId: string, signal?: AbortSignal) =>
      axios
        .get<GetCountByQueryResponse>(`${BASE}/data/${dataQueryId}/count`, {
          ...AXIOS_BASE_CONFIG,
          signal,
        })
        .then((response) => response.data.totalRows)
        .catch(ctx.handleApiError),
    getDataIdsByQuery: (
      dataQueryId: string,
      offset: number,
      limit: number,
      hasSmartSearch: boolean
    ) =>
      axios
        .get<GetDataIdsByQuery[]>(
          buildDataUrl(dataQueryId, '/id', {offset, limit, hasSmartSearch}),
          AXIOS_BASE_CONFIG
        )
        .then((response) => response.data)
        .catch(ctx.handleApiError),
    getDataQuery: (dataQueryId: GetDataQueryRequest) =>
      axios
        .get<GetDataQueryResponse>(`${BASE}/data-query/${dataQueryId}`, AXIOS_BASE_CONFIG)
        .then((response) => response.data)
        .catch((error: Error & {response?: {data: unknown}}) => {
          const errorData = error?.response?.data;
          const isErrorDG0010 =
            isApiException(errorData) &&
            errorData?.errors?.some((error) => error.code === 'PKG-DG-0010');

          if (isErrorDG0010) {
            throw error;
          }

          ctx.handleApiError(error);
        }),
  };
}
